import {
  AxiosInstance,
  AxiosError,
  AxiosRequestConfig,
} from 'axios';

import Cookies from 'js-cookie';

import { COOKIE_NAMES } from '../constants';

import * as services from '../services';

import Emitter from '../modules/Emitter.module';

import { AppEvent } from '../background-components/EventHandler';

interface RetryRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

export class AxiosUtil {
  /**
   * Only one refresh request can run at a time.
   *
   * All requests that need the new token will wait for
   * this promise instead of creating another refresh request.
   */
  private static refreshPromise: Promise<string> | null = null;

  /**
   * Prevent requests from trying to refresh when the session
   * has already been determined to be invalid.
   */
  private static isSessionInvalid = false;

  /**
   * Update Axios default Authorization header.
   */
  private static syncAuthorizationHeader(
    axiosInstance: AxiosInstance,
    accessToken?: string,
  ) {
    const headers = axiosInstance.defaults.headers as any;

    const authHeader = accessToken
      ? `Bearer ${accessToken}`
      : undefined;

    headers.Authorization = authHeader;

    headers.common = {
      ...headers.common,
      Authorization: authHeader,
    };

    if (!authHeader) {
      delete headers.Authorization;

      if (headers.common) {
        delete headers.common.Authorization;
      }
    }
  }

  /**
   * Get the latest access token from cookie.
   */
  private static getCurrentAccessToken(): string | undefined {
    return Cookies.get(
      COOKIE_NAMES.COOKIE_ACCESS_TOKEN,
    );
  }

  /**
   * Get the access token that was used by a request.
   */
  private static getRequestAccessToken(
    config: AxiosRequestConfig,
  ): string | undefined {
    const headers = config.headers as any;

    const authorization =
      headers?.Authorization ??
      headers?.authorization ??
      (
        typeof headers?.get === 'function'
          ? headers.get('Authorization')
          : undefined
      );

    if (typeof authorization !== 'string') {
      return undefined;
    }

    return authorization.replace(
      /^Bearer\s+/i,
      '',
    );
  }

  /**
   * Reset session state after login.
   */
  static resetSessionState() {
    AxiosUtil.isSessionInvalid = false;
    AxiosUtil.refreshPromise = null;
  }

  /**
   * Refresh access token.
   *
   * This method guarantees that only one refresh request
   * is running at a time.
   */
  private static async refreshAccessToken(): Promise<string> {
    if (AxiosUtil.isSessionInvalid) {
      throw new Error(
        'Session is invalid; skipping refresh token retry.',
      );
    }

    /**
     * If another request is already refreshing the token,
     * wait for the same promise.
     */
    if (AxiosUtil.refreshPromise) {
      return AxiosUtil.refreshPromise;
    }

    AxiosUtil.refreshPromise = (async () => {
      const refreshToken = Cookies.get(
        COOKIE_NAMES.COOKIE_REFRESH_TOKEN,
      );

      if (!refreshToken) {
        throw new Error(
          'Refresh token not found',
        );
      }

      const response =
        await services.refreshToken(refreshToken);

      const data = response?.data?.data;

      if (!data?.accessToken) {
        throw new Error(
          'Refresh response does not contain access token',
        );
      }

      /**
       * Save NEW access token.
       */
      Cookies.set(
        COOKIE_NAMES.COOKIE_ACCESS_TOKEN,
        data.accessToken,
        {
          expires: 30,
        },
      );

      /**
       * Keep the existing refresh-token behavior.
       *
       * Backend currently does not rotate refresh token,
       * so this does not replace it with a new token.
       */
      if (data.refreshToken) {
        Cookies.set(
          COOKIE_NAMES.COOKIE_REFRESH_TOKEN,
          refreshToken,
          {
            expires: 30,
          },
        );
      }

      Cookies.set(
        COOKIE_NAMES.COOKIE_EXPIRES_IN,
        data.accessTokenExpiresIn
          ? String(data.accessTokenExpiresIn)
          : '',
        {
          expires: 30,
        },
      );

      AxiosUtil.isSessionInvalid = false;

      return data.accessToken;
    })();

    try {
      return await AxiosUtil.refreshPromise;
    } finally {
      /**
       * Important:
       * after refresh finishes, allow future refreshes.
       */
      AxiosUtil.refreshPromise = null;
    }
  }

  /**
   * Request interceptor.
   *
   * 1. If a refresh is already running, wait for TOKEN_B.
   * 2. Always use the latest access token from cookie.
   */
  private static handleAuthorizationHeader(
    axiosInstance: AxiosInstance,
  ) {
    axiosInstance.interceptors.request.use(
      async (config) => {
        const url = config.url || '';

        const isRefreshAPI =
          url.includes('/access-token');

        const isLogoutAPI =
          url.includes('/logout');

        /**
         * Never wait for refresh on the refresh API itself.
         *
         * Otherwise:
         *
         * refresh API
         *    ↓
         * request interceptor
         *    ↓
         * waits for refreshPromise
         *    ↓
         * deadlock
         */
        if (
          !isRefreshAPI &&
          !isLogoutAPI &&
          AxiosUtil.refreshPromise
        ) {
          try {
            /**
             * Wait until TOKEN_B is available.
             */
            await AxiosUtil.refreshPromise;
          } catch {
            /**
             * Let the original request continue.
             * The response interceptor will handle the error.
             */
          }
        }

        /**
         * Get the latest token AFTER waiting for refresh.
         */
        const accessToken =
          AxiosUtil.getCurrentAccessToken();

        if (accessToken) {
          config.headers =
            config.headers || {};

          config.headers.Authorization =
            `Bearer ${accessToken}`;
        }

        return config;
      },
    );
  }

  static handleAutoRefreshToken(
    axiosInstance: AxiosInstance,
  ) {
    /**
     * Register request queue first.
     */
    AxiosUtil.handleAuthorizationHeader(
      axiosInstance,
    );

    /**
     * Response interceptor.
     */
    axiosInstance.interceptors.response.use(
      (response) => response,

      async (error: AxiosError) => {
        const config = error.config as
          | RetryRequestConfig
          | undefined;

        if (!config) {
          return Promise.reject(error);
        }

        const status =
          error.response?.status;

        const url =
          config.url || '';

        const is401 =
          status === 401;

        const isRefreshAPI =
          url.includes('/access-token');

        const isLogoutAPI =
          url.includes('/logout');

        /**
         * Session has already been determined invalid.
         */
        if (AxiosUtil.isSessionInvalid) {
          return Promise.reject(error);
        }

        /**
         * Refresh API itself returned 401.
         *
         * This means the refresh token is invalid/expired.
         */
        if (
          is401 &&
          isRefreshAPI
        ) {
          AxiosUtil.isSessionInvalid = true;

          Emitter.emit(
            AppEvent.onRefreshToken401,
            {},
          );

          return Promise.reject(error);
        }

        /**
         * Only handle normal API 401.
         */
        if (
          !is401 ||
          isRefreshAPI ||
          isLogoutAPI ||
          config._retry
        ) {
          return Promise.reject(error);
        }

        /**
         * --------------------------------------------------
         * CASE 1:
         * Another request is already refreshing.
         * --------------------------------------------------
         *
         * Wait for TOKEN_B instead of starting another
         * refresh request.
         */
        if (AxiosUtil.refreshPromise) {
          try {
            const newAccessToken =
              await AxiosUtil.refreshPromise;

            config._retry = true;

            config.headers = {
              ...config.headers,
              Authorization:
                `Bearer ${newAccessToken}`,
            };

            return axiosInstance.request(
              config,
            );
          } catch (refreshError) {
            AxiosUtil.isSessionInvalid = true;

            const refreshStatus =
              (refreshError as any)
                ?.response?.status;

            if (
              refreshStatus === 401 ||
              refreshStatus === 403
            ) {
              Emitter.emit(
                AppEvent.onRefreshToken401,
                {},
              );
            }

            return Promise.reject(
              refreshError,
            );
          }
        }

        /**
         * --------------------------------------------------
         * CASE 2:
         * This request used an old token.
         *
         * Example:
         *
         * request TOKEN_A
         * cookie  TOKEN_B
         *
         * TOKEN_B means another request has already
         * completed the refresh.
         *
         * Therefore:
         * - DO NOT refresh again
         * - replace TOKEN_A with TOKEN_B
         * - retry
         * --------------------------------------------------
         */
        const currentAccessToken =
          AxiosUtil.getCurrentAccessToken();

        const requestAccessToken =
          AxiosUtil.getRequestAccessToken(
            config,
          );

        if (
          currentAccessToken &&
          requestAccessToken &&
          currentAccessToken !==
            requestAccessToken
        ) {
          config._retry = true;

          config.headers = {
            ...config.headers,
            Authorization:
              `Bearer ${currentAccessToken}`,
          };

          return axiosInstance.request(
            config,
          );
        }

        /**
         * --------------------------------------------------
         * CASE 3:
         * No refresh is running and this request really
         * needs a new access token.
         * --------------------------------------------------
         */
        config._retry = true;

        try {
          const newAccessToken =
            await AxiosUtil.refreshAccessToken();

          /**
           * Update Axios defaults.
           */
          AxiosUtil.syncAuthorizationHeader(
            axiosInstance,
            newAccessToken,
          );

          /**
           * IMPORTANT:
           * Replace TOKEN_A in the failed request
           * with TOKEN_B.
           */
          config.headers = {
            ...config.headers,
            Authorization:
              `Bearer ${newAccessToken}`,
          };

          /**
           * Retry original request with TOKEN_B.
           */
          return axiosInstance.request(
            config,
          );
        } catch (refreshError: any) {
          /**
           * Only mark the session invalid when the
           * refresh API itself says the token is invalid.
           */
          const refreshStatus =
            refreshError?.response?.status;

          if (
            refreshStatus === 401 ||
            refreshStatus === 403
          ) {
            AxiosUtil.isSessionInvalid = true;

            Emitter.emit(
              AppEvent.onRefreshToken401,
              {},
            );
          }

          return Promise.reject(
            refreshError || error,
          );
        }
      },
    );
  }
}
